import { Text, View, TouchableOpacity } from 'react-native';
import { useEffect, useMemo, useState, useCallback } from 'react';
import { TabView } from 'react-native-tab-view';
import ReachTabBar from '@/components/General/ReachTabBar';
import NotificationBadge from '@/components/General/NotificationBadge';
import { StyleSheet } from 'react-native-unistyles';
import { Query } from '@/utils/react-query/Queries';
import useNotificationBadges from '@/utils/hooks/useNotificationBadges';
import { useRouter, useNavigation } from 'expo-router';
import usePlatform from '@/utils/hooks/usePlatform';
import BoardsList from '@/components/Connect/Boards/BoardsList';
import ConversationsList from '@/components/Connect/Conversations/ConversationsList';
import SMSCampaignsList from '@/components/Connect/Campaigns/SMSCampaignsList';
import EmailCampaignsList from '@/components/Connect/Campaigns/EmailCampaignsList';
import AnnouncementsList from '@/components/Connect/Announcements/AnnouncementsList';
import Unicon from '@/components/General/Unicon';
import DropdownMenu, { dropdownIcons } from '@/components/General/DropdownMenu';
import SolidButton from '@/components/General/SolidButton';

const CONNECT_ROUTES = [
    { key: 'boards', title: 'Boards' },
    { key: 'announcements', title: 'Announcements' },
    { key: 'conversations', title: 'Conversations' },
    { key: 'sms-campaigns', title: 'SMS Campaigns' },
    { key: 'email-campaigns', title: 'Email Campaigns' },
];

//Persist tab index across remounts (web unmounts on navigation, native keeps in stack)
let persistedTabIndex = 0;

export default function ConnectScreen() {
    const router = useRouter();
    const navigation = useNavigation();
    const { isNative } = usePlatform();

    const [tabIndex, setTabIndex] = useState(persistedTabIndex);
    //Non-persistent boards filters — reset on remount.
    const [showHiddenBoards, setShowHiddenBoards] = useState(false);
    const [showArchivedBoards, setShowArchivedBoards] = useState(false);
    const handleTabIndexChange = useCallback((index) => {
        persistedTabIndex = index;
        setTabIndex(index);
    }, []);

    const { data: loggedInUser } = Query.people.loggedInUser();
    const { boardsUnread, conversationsUnread } = useNotificationBadges();

    // const dateOfBirth = Math.floor(Math.abs((DateTime.fromISO(loggedInUser?.dateOfBirth).setLocale('en').diffNow('years').years)));
    const canViewConversations = loggedInUser?.can?.("View Conversations");
    const canViewSMSCampaigns = loggedInUser?.can?.("Send SMS Messages");
    const canViewEmailCampaigns = loggedInUser?.can?.("Send Mass Emails");

    //Filter tabs out if no permission — adjust routes
    const routes = useMemo(() => {
        return CONNECT_ROUTES.filter(route => {
            if(route.key == 'conversations') return canViewConversations;
            if(route.key == 'sms-campaigns' || route.key == 'announcements') return canViewSMSCampaigns;
            if(route.key == 'email-campaigns') return canViewEmailCampaigns;
            return true;
        });
    }, [canViewConversations, canViewSMSCampaigns, canViewEmailCampaigns]);

    //Per-tab badge counts keyed by route key — fed to ReachTabBar via `options`
    //so react-native-tab-view renders them inline with the tab label.
    const badgeCounts = useMemo(() => ({
        boards: boardsUnread,
        conversations: conversationsUnread,
        'sms-campaigns': 0,
        'email-campaigns': 0,
    }), [boardsUnread, conversationsUnread]);

    const tabOptions = useMemo(() => {
        const options = {};
        routes.forEach(route => {
            options[route.key] = {
                label: ({ labelText, color }) => (
                    <View style={styles.tabLabel}>
                        <Text style={{ color }} numberOfLines={1}>{labelText}</Text>
                        <NotificationBadge count={badgeCounts[route.key] || 0} size={16} testID={`connect-tab-badge-${route.key}`} />
                    </View>
                ),
            };
        });
        return options;
    }, [routes, badgeCounts]);

    //Clamp tabIndex if routes shrink — also reset persisted value so a stale
    //index from a previous mount can't outlive a permission change.
    useEffect(() => {
        if(tabIndex >= routes.length) {
            persistedTabIndex = 0;
            setTabIndex(0);
        }
    }, [routes.length, tabIndex]);

    //Header buttons — change per active tab
    useEffect(() => {
        const activeTab = routes[tabIndex]?.key;

        let buttons = null;
        if(activeTab == 'boards') {
            const boardFilterOptions = [
                ...(canViewSMSCampaigns ? [
                    {
                        name: 'New announcement',
                        icon: dropdownIcons.megaphone,
                        hideCheckbox: true,
                        dismissOnSelect: true,
                        onSelect: () => router.push('/connect/announcements/create'),
                    },
                    { separator: true },
                ] : []),
                {
                    name: showHiddenBoards ? 'Hide Hidden Boards' : 'Show Hidden Boards',
                    icon: showHiddenBoards ? dropdownIcons.hide : dropdownIcons.unhide,
                    selected: showHiddenBoards,
                    onSelect: () => setShowHiddenBoards(v => !v),
                },
                {
                    name: showArchivedBoards ? 'Hide Archived Boards' : 'Show Archived Boards',
                    icon: dropdownIcons.archive,
                    selected: showArchivedBoards,
                    onSelect: () => setShowArchivedBoards(v => !v),
                },
            ];
            buttons = (
                <>
                    <DropdownMenu multiple testID="boards-filter-menu" options={boardFilterOptions} />
                    {loggedInUser?.can?.("Create Boards") && (
                        <TouchableOpacity testID="connect-add" onPress={() => router.push("/connect/boards/create")}>
                            <Unicon name="add" size={28} colorVariant="text" />
                        </TouchableOpacity>
                    )}
                </>
            );
        }
        else if(activeTab == 'announcements' && canViewSMSCampaigns) {
            buttons = (
                <DropdownMenu
                    testID="connect-add"
                    options={[
                        { name: 'New announcement', icon: dropdownIcons.megaphone, onSelect: () => router.push('/connect/announcements/create') },
                        { name: 'Announcement settings', icon: dropdownIcons.settings, onSelect: () => router.push('/connect/announcements/settings') },
                    ]}
                />
            );
        }
        else if(activeTab == 'conversations' && loggedInUser?.can?.("Create Conversations")) {
            buttons = (
                <TouchableOpacity testID="connect-add" onPress={() => router.push("/connect/conversations/create")}>
                    <Unicon name="add" size={28} colorVariant="text" />
                </TouchableOpacity>
            );
        }
        else if(activeTab == 'sms-campaigns' && loggedInUser?.can?.("Send SMS Messages")) {
            //Compose via people batch actions
            buttons = (
                <SolidButton
                    testID="connect-add"
                    accessibilityLabel="New SMS campaign"
                    variant="muted"
                    type="primary"
                    size="sm"
                    text={isNative ? '' : 'New SMS'}
                    iconName="add"
                    iconBefore
                    onPress={() => router.push({ pathname: '/people', params: { compose: 'sms' } })}
                />
            );
        }
        else if(activeTab == 'email-campaigns' && loggedInUser?.can?.("Send Mass Emails")) {
            buttons = (
                <DropdownMenu
                    testID="email-campaigns-menu"
                    options={[
                        {
                            name: 'New Email',
                            icon: { android: 'email', ios: 'envelope', web: 'mail-outline' },
                            onSelect: () => router.push({ pathname: '/people', params: { compose: 'email' } }),
                        },
                        {
                            name: 'Email Builder',
                            icon: { android: 'build', ios: 'hammer', web: 'construct-outline' },
                            onSelect: () => router.push('/emails/editor'),
                        },
                    ]}
                />
            );
        }

        navigation.setOptions({
            headerRight: !buttons ? null : () => (
                <View style={styles.headerRightContainer}>
                    {buttons}
                </View>
            ),
        });
    }, [navigation, tabIndex, routes, loggedInUser, showHiddenBoards, showArchivedBoards, isNative]);

    const renderTabBar = useCallback((props) => <ReachTabBar {...props} options={tabOptions} />, [tabOptions]);

    const renderScene = ({ route: tabRoute }) => {
        const scene = tabRoute.key == 'boards' ? <BoardsList display="hero" showHidden={showHiddenBoards} showArchived={showArchivedBoards} />
            : tabRoute.key == 'conversations' ? <ConversationsList />
            : tabRoute.key == 'sms-campaigns' ? <SMSCampaignsList />
            : tabRoute.key == 'email-campaigns' ? <EmailCampaignsList />
            : tabRoute.key == 'announcements' ? <AnnouncementsList />
            : null;

        //Campaign tabs render tables on web — give them full width
        const isCampaignTab = tabRoute.key == 'sms-campaigns' || tabRoute.key == 'email-campaigns';

        return (
            <View style={styles.sceneContainer}>
                <View style={[styles.maxWidthContainer, (isCampaignTab || tabRoute.key == 'boards') && styles.fullWidthContainer]}>
                    {scene}
                </View>
            </View>
        );
    };

    return (
        <View style={styles.container}>
            <TabView
                swipeEnabled={isNative}
                navigationState={{ index: Math.min(tabIndex, routes.length - 1), routes }}
                onIndexChange={handleTabIndexChange}
                renderTabBar={renderTabBar}
                renderScene={renderScene}
            />
        </View>
    );
}

const styles = StyleSheet.create(({ colors }) => ({
    container: {
        flex: 1,
        backgroundColor: 'transparent',
    },
    tabLabel: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 6,
    },
    headerRightContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 12,
    },
    sceneContainer: {
        flex: 1,
        alignItems: "center",
    },
    maxWidthContainer: {
        flex: 1,
        width: "100%",
        maxWidth: 700,
        padding: 10,
    },
    fullWidthContainer: {
        maxWidth: "100%",
    },
    noAccessContainer: {
        flex: 1,
        alignItems: "center",
        justifyContent: "center",
    },
    noAccessText: {
        color: colors.textSecondary,
    },
}));
